import { DeleteOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import classNames from 'classnames';
import { useState } from 'react';
import { toast } from 'react-toastify';

import {
  DeletePortalBorrowerAchAccountDocument,
  PortalBorrowerAchAccountFragment,
} from '@willow/graphql-iso/src/portal';
import { BankDeleteConfirmModal, Button } from '@willow/shared-web';
import { Form, OverlayTrigger, Tooltip } from '@willow/shared-web/bootstrap';
import { CompanyId, LoanId } from '@willow/types-iso';

import { NachaAddAccount } from './NachaAddAccount';

interface Props {
  achAccounts: PortalBorrowerAchAccountFragment[];
  onBankUpdate: (bank: PortalBorrowerAchAccountFragment | undefined) => void;
  companyId: CompanyId;
  loanId: LoanId;
}

export const NachaPaymentMethod = ({ achAccounts, onBankUpdate, companyId, loanId }: Props) => {
  const firstEnabledIndex = achAccounts.findIndex((a) => !a.disabledAt);
  const defaultIndex = firstEnabledIndex === -1 ? 0 : firstEnabledIndex;

  const [showAddAccount, setShowAddAccount] = useState(!achAccounts.length);
  const [selectedAccountIndex, setSelectedAccountIndex] = useState(defaultIndex);
  const [prevAchAccounts, setPrevAchAccounts] = useState(achAccounts);

  if (achAccounts !== prevAchAccounts) {
    setPrevAchAccounts(achAccounts);
    setSelectedAccountIndex(defaultIndex);
    onBankUpdate(firstEnabledIndex === -1 ? undefined : achAccounts[defaultIndex]);
  }

  const [accountToDelete, setAccountToDelete] = useState<PortalBorrowerAchAccountFragment | undefined>(undefined);
  const [deleteAchAccount] = useMutation(DeletePortalBorrowerAchAccountDocument);

  const onBankSelectUpdate = (index: number) => {
    setSelectedAccountIndex(index);
    onBankUpdate(achAccounts[index]);
  };

  return (
    <div className="payment-method make-payment__container0">
      {achAccounts.map((account, index) => {
        const isSelected = index === selectedAccountIndex;
        const isDisabled = !!account.disabledAt;

        return (
          <Form.Check
            type="radio"
            key={`account-${index}`}
            id={`account-${account.id}`}
            className={classNames(['payment-form__radio'], {
              'payment-form__radio_active': isSelected && !isDisabled,
            })}
          >
            <Form.Check.Input
              type="radio"
              name="payment-bank"
              value={account.id}
              checked={isSelected && !isDisabled}
              disabled={isDisabled}
              onChange={() => onBankSelectUpdate(index)}
            />

            <Form.Check.Label>
              <div className="bank-container__label">
                {account.accountName} ({account.lastFour}){' '}
                {isDisabled && <span className="u-color-red1 fst-italic">Disabled</span>}
              </div>
              <button
                type="button"
                onClick={() => {
                  setAccountToDelete(account);
                }}
                disabled={account.addedByCompany}
                className="bank-container__trash make-payment__container0 d-flex p-2"
                aria-label={`Click to permanently remove your bank account for ${account.accountName}`}
              >
                {account.addedByCompany ? (
                  <OverlayTrigger
                    trigger={['click', 'focus', 'hover']}
                    rootClose
                    overlay={
                      <Tooltip>
                        This account was added by your servicer. If you believe this is an error, please contact your
                        servicer immediately
                      </Tooltip>
                    }
                  >
                    <DeleteOutlined />
                  </OverlayTrigger>
                ) : (
                  <DeleteOutlined />
                )}
              </button>
            </Form.Check.Label>
          </Form.Check>
        );
      })}

      {accountToDelete && (
        <BankDeleteConfirmModal
          showModal={!!accountToDelete}
          onCancel={() => {
            setAccountToDelete(undefined);
          }}
          onConfirm={async () => {
            try {
              await deleteAchAccount({ variables: { companyId, loanId, accountId: accountToDelete.id } });
              setAccountToDelete(undefined);
              toast.success('Account deleted successfully');
            } catch {
              toast.error('Failed to delete account');
            }
          }}
        />
      )}

      <div className="p-3">
        {showAddAccount ? (
          <NachaAddAccount
            companyId={companyId}
            loanId={loanId}
            onClose={() => {
              setShowAddAccount(false);
            }}
          />
        ) : (
          <Button
            size="sm"
            variant="secondary"
            onClick={(e) => {
              e.preventDefault();
              setShowAddAccount(!showAddAccount);
            }}
          >
            + Add Account
          </Button>
        )}
      </div>
    </div>
  );
};
